// Centralised image catalogue for Mutha Paras & Co.
// Source files live in /app/backend/static/gallery/{lg,sm}/
// Served by FastAPI at {REACT_APP_BACKEND_URL}/api/gallery-static/{lg|sm}/{filename}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const lg = (f) => `${API}/gallery-static/lg/${f}`;
const sm = (f) => `${API}/gallery-static/sm/${f}`;

// ============ COLOUR CHARTS (Master 1-156) ============
export const colourCharts = [
  { file: "chart-156-shades-part1.jpg", name: "Master Shade Chart (Part 1)" },
  { file: "chart-156-shades.jpg", name: "Master Shade Chart (Part 2)" },
];

export const colourChart = {
  filename: colourCharts[0].file,
  name: colourCharts[0].name,
  url: lg(colourCharts[0].file),
  thumb: sm(colourCharts[0].file),
};

// ============ AMERICAN CREPE CHARTS (19 charts, ~760 shades) ============
export const americanCrepeCharts = Array.from({ length: 19 }, (_, i) => {
  const num = String(i + 1).padStart(2, "0");
  return {
    file: `crepe-${num}.jpg`,
    label: `American Crepe — Chart ${i + 1}`,
  };
});

// ============ LINING SHADE CHARTS (4 charts, ~150 shades) ============
export const liningCharts = Array.from({ length: 4 }, (_, i) => {
  const num = String(i + 1).padStart(2, "0");
  return {
    file: `lining-chart-${num}.jpg`,
    label: `Lining — Chart ${i + 1}`,
  };
});

// ============ PRODUCT FABRIC PHOTOS ============
export const productPhotos = {
  "600-lining": [
    { file: "600-sd-sample.jpg", label: "600 SD — Sample" },
    { file: "600-sd-width.jpg", label: "600 SD — Width" },
  ],
  "060-lining": [
    { file: "060-dd-sample.jpg", label: "060 DD — Sample" },
    { file: "060-dd-width.jpg", label: "060 DD — Width" },
  ],
  "006-lining": [
    { file: "006-mercerised-sample.jpg", label: "006 DD Mercerised — Sample" },
    { file: "006-mercerised-width.jpg", label: "006 DD Mercerised — Width" },
  ],
  "6000-shrinkless": [
    { file: "6000-sd-sample.jpg", label: "6000 SD — Sample" },
    { file: "6000-sd-width.jpg", label: "6000 SD — Width" },
  ],
  "303-pure-cotton": [
    { file: "303-cotton-sample.jpg", label: "303 Cotton 2x1 — Sample" },
    { file: "303-cotton-width.jpg", label: "303 Cotton 2x1 — Width" },
  ],
  "3000-pure-cotton": [
    { file: "3000-cotton-sample.jpg", label: "3000 Cotton 2x1 — Sample" },
    { file: "3000-cotton-width.jpg", label: "3000 Cotton 2x1 — Width" },
  ],
  "american-crepe": [
    { file: "american-crepe-sample.jpg", label: "American Crepe — Sample" },
    { file: "american-crepe-width.jpg", label: "American Crepe — Width" },
  ],
};

export const getProductPhotos = (productId) =>
  (productPhotos[productId] || []).map((p) => ({
    url: lg(p.file),
    thumb: sm(p.file),
    alt: p.label,
  }));

export const getCrepeChartUrl = (file) => ({ url: lg(file), thumb: sm(file) });
export const getLiningChartUrl = (file) => ({ url: lg(file), thumb: sm(file) });

// ============ STORE PHOTOS ============
const storePhotos = Array.from({ length: 25 }, (_, i) => ({
  file: `store-${String(i + 1).padStart(2, "0")}.jpg`,
  label: `Showroom & Store View ${i + 1}`,
}));

const showroomPhotos = Array.from({ length: 5 }, (_, i) => ({
  file: `showroom-${String(i + 1).padStart(2, "0")}.jpg`,
  label: `Latest Showroom View ${i + 1}`,
}));

// ============ UNIFIED GALLERY ENTRIES ============
export const galleryEntries = [
  // Master charts (featured)
  ...colourCharts.map((c, idx) => ({
    category: "chart",
    url: lg(c.file),
    thumb: sm(c.file),
    alt: c.name,
    feature: idx === 0,
  })),
  // American Crepe charts
  ...americanCrepeCharts.map((c) => ({
    category: "crepe",
    url: lg(c.file),
    thumb: sm(c.file),
    alt: c.label,
  })),
  // Lining shade charts
  ...liningCharts.map((c) => ({
    category: "lining",
    url: lg(c.file),
    thumb: sm(c.file),
    alt: c.label,
  })),
  // Product photos
  ...Object.entries(productPhotos).flatMap(([productId, photos]) =>
    photos.map((p) => ({
      category: "products",
      productId,
      url: lg(p.file),
      thumb: sm(p.file),
      alt: p.label,
    }))
  ),
  // Showroom (newest)
  ...showroomPhotos.map((p) => ({
    category: "showroom",
    url: lg(p.file),
    thumb: sm(p.file),
    alt: p.label,
  })),
  // Store
  ...storePhotos.map((p) => ({
    category: "store",
    url: lg(p.file),
    thumb: sm(p.file),
    alt: p.label,
  })),
];

export const galleryCategories = [
  { id: "all", label: "All Photos" },
  { id: "chart", label: "Master Shade Chart" },
  { id: "lining", label: "Lining Charts" },
  { id: "crepe", label: "American Crepe Charts" },
  { id: "showroom", label: "Showroom" },
  { id: "store", label: "Store & Shop" },
  { id: "products", label: "Product Samples" },
];
